<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { searchAddresses, formatAddress, mapResultToPlaceDetails } from '@/services/addressSearchService';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: 'Endereço'
  },
  placeholder: {
    type: String,
    default: 'Digite um endereço'
  },
  required: {
    type: Boolean,
    default: false
  },
  variant: {
    type: String,
    default: 'outlined'
  },
  clearable: {
    type: Boolean,
    default: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  density: {
    type: String,
    default: 'comfortable'
  }
});

const emit = defineEmits(['update:modelValue', 'place-selected', 'input', 'clear', 'copy-text']);

const searchInput = ref(props.modelValue);
const suggestions = ref([]);
const loading = ref(false);
const menuOpen = ref(false);
const selectedPlace = ref(null);
const debounceTimeout = ref(null);
const inputField = ref(null);

// Computed property para mostrar o menu apenas quando houver sugestões
const showMenu = computed(() => menuOpen.value && suggestions.value.length > 0);

// Nossa implementação de debounce
const debounce = (fn, delay) => {
  return (...args) => {
    if (debounceTimeout.value) clearTimeout(debounceTimeout.value);
    debounceTimeout.value = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

const debouncedSearch = debounce(async (query) => {
  if (!query || query.length < 3) {
    suggestions.value = [];
    menuOpen.value = false;
    return;
  }

  loading.value = true;
  try {
    const response = await searchAddresses(query);
    const results = response.resultados || [];
    
    if (results.length > 0) {
      // Agrupar resultados por endereço + cidade para evitar duplicatas
      const uniqueResults = [];
      const seen = new Set();
      
      results.forEach(item => {
        const key = `${item.logradouro}-${item.localidade}-${item.CEP}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueResults.push(item);
        }
      });
      
      suggestions.value = uniqueResults;
      menuOpen.value = true;
    } else {
      suggestions.value = [];
      menuOpen.value = false;
    }
  } catch (error) {
    console.error('Erro na busca de endereços:', error);
    suggestions.value = [];
    menuOpen.value = false;
  } finally {
    loading.value = false;
  }
}, 300);

// Função para atualizar o input e emitir eventos
const updateInput = (value) => {
  searchInput.value = value;
  emit('update:modelValue', value);
  emit('input', value);
};

// Função para lidar com a seleção de um endereço
const selectAddress = (result) => {
  menuOpen.value = false;
  
  // Formatar o endereço para exibição
  const formattedAddress = formatAddress(result);
  updateInput(formattedAddress);
  
  // Mapear o resultado para o formato esperado e emitir o evento
  const details = mapResultToPlaceDetails(result);
  selectedPlace.value = details;
  emit('place-selected', details);
};

// Função para limpar o input
const clearInput = () => {
  updateInput('');
  selectedPlace.value = null;
  suggestions.value = [];
  menuOpen.value = false;
  emit('clear');
};

// Função para tratar a entrada do usuário
const onInputChange = () => {
  if (searchInput.value) {
    debouncedSearch(searchInput.value);
  } else {
    suggestions.value = [];
    menuOpen.value = false;
  }
};

// Observar alterações no modelValue (caso seja alterado externamente)
watch(() => props.modelValue, (newVal) => {
  if (newVal !== searchInput.value) {
    searchInput.value = newVal;
  }
});

// Cancelar debounce quando o componente for desmontado
onUnmounted(() => {
  if (debounceTimeout.value) {
    clearTimeout(debounceTimeout.value);
  }
});

// Inicialização
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Fechar o menu quando clicar fora do componente
const handleClickOutside = (event) => {
  const el = inputField.value?.$el;
  if (el && !el.contains(event.target) && menuOpen.value) {
    menuOpen.value = false;
  }
};

// Função para copiar texto para área de transferência
const copyText = (text, description) => {
  navigator.clipboard.writeText(text).then(() => {
    emit('copy-text', { text: description, value: text });
  }).catch(err => {
    console.error('Erro ao copiar texto:', err);
  });
};

// Portal for the dropdown
const dropdownPortalId = `custom-address-autocomplete-dropdown-${Date.now()}`;
let dropdownPortalElement = null;

// Position the dropdown relative to the input field
const positionDropdown = () => {
  if (!showMenu.value || !inputField.value || !dropdownPortalElement) return;

  const inputRect = inputField.value.$el.getBoundingClientRect();

  dropdownPortalElement.style.position = 'fixed';
  dropdownPortalElement.style.width = `${inputRect.width}px`;
  dropdownPortalElement.style.left = `${inputRect.left}px`;
  dropdownPortalElement.style.top = `${inputRect.bottom + window.scrollY}px`;
  dropdownPortalElement.style.maxHeight = '300px';
  dropdownPortalElement.style.overflowY = 'auto';
  dropdownPortalElement.style.zIndex = '999999';
  dropdownPortalElement.style.backgroundColor = 'white';
  dropdownPortalElement.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
  dropdownPortalElement.style.borderRadius = '8px';
};

// Render suggestions in the dropdown portal
const renderSuggestions = () => {
  if (!dropdownPortalElement) return;

  if (showMenu.value) {
    dropdownPortalElement.innerHTML = '';

    const list = document.createElement('div');
    list.className = 'suggestions-list';

    suggestions.value.forEach((suggestion) => {
      const item = document.createElement('div');
      item.className = 'suggestion-item';

      const title = document.createElement('div');
      title.className = 'suggestion-title';
      title.textContent = suggestion.logradouro;

      const subtitle = document.createElement('div');
      subtitle.className = 'suggestion-subtitle';
      subtitle.textContent = `${suggestion.localidade}, ${suggestion.UF} - ${suggestion.CEP}`;

      item.appendChild(title);
      item.appendChild(subtitle);

      item.addEventListener('click', () => {
        selectAddress(suggestion);
      });

      list.appendChild(item);
    });

    if (loading.value && suggestions.value.length === 0) {
      const loadingItem = document.createElement('div');
      loadingItem.className = 'suggestion-loading';
      loadingItem.textContent = 'Buscando...';
      list.appendChild(loadingItem);
    }

    dropdownPortalElement.appendChild(list);
    dropdownPortalElement.style.display = 'block';
    positionDropdown();
  } else {
    dropdownPortalElement.style.display = 'none';
  }
};

// Watchers for rendering suggestions
watch(showMenu, () => renderSuggestions());
watch(suggestions, () => renderSuggestions(), { deep: true });
watch(loading, () => renderSuggestions());

// Create and remove the dropdown portal
onMounted(() => {
  dropdownPortalElement = document.createElement('div');
  dropdownPortalElement.id = dropdownPortalId;
  dropdownPortalElement.style.display = 'none';
  document.body.appendChild(dropdownPortalElement);

  const style = document.createElement('style');
  style.textContent = `
    #${dropdownPortalId} .suggestions-list {
      padding: 8px 0;
    }
    #${dropdownPortalId} .suggestion-item {
      padding: 12px 16px;
      cursor: pointer;
      display: flex;
      flex-direction: column;
    }
    #${dropdownPortalId} .suggestion-item:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
    #${dropdownPortalId} .suggestion-title {
      font-weight: 500;
      font-size: 0.875rem;
    }
    #${dropdownPortalId} .suggestion-subtitle {
      font-size: 0.75rem;
      color: rgba(0, 0, 0, 0.6);
    }
    #${dropdownPortalId} .suggestion-loading {
      padding: 12px 16px;
      text-align: center;
      font-style: italic;
      color: rgba(0, 0, 0, 0.6);
    }
  `;
  document.head.appendChild(style);
});

onUnmounted(() => {
  if (dropdownPortalElement) {
    document.body.removeChild(dropdownPortalElement);
  }
});
</script>

<template>
  <div class="custom-address-autocomplete">
    <v-text-field
      ref="inputField"
      v-model="searchInput"
      :label="label"
      :placeholder="placeholder"
      :variant="variant"
      :density="density"
      :clearable="clearable"
      :disabled="disabled"
      :required="required"
      :loading="loading"
      @click:clear="clearInput"
      @focus="menuOpen = true"
      @input="onInputChange"
      prepend-inner-icon="mdi-database-search"
      hide-details="auto"
      autocomplete="off"
    >
      <template v-slot:append>
        <v-progress-circular
          v-if="loading"
          indeterminate
          size="20"
          width="2"
          color="primary"
        ></v-progress-circular>
      </template>
    </v-text-field>
  </div>
</template>

